import { useIntersectionObserver } from '@packages/shared/src/hooks/useIntersectionObserver/useIntersectionObserver';
import { useEffect, useRef } from 'react';

export interface StickyProps {
  children: React.ReactNode;
  className?: string;
  onViewportChange?: (isInViewport: boolean) => void;
}

export const StickyInsideParent: React.FC<StickyProps> = ({
  children,
  className,
  onViewportChange,
  ...props
}) => {
  useEffect(() => {
    if (onViewportChange) {
      onViewportChange(true);
    }
  }, [onViewportChange]);

  return (
    <div className={`sticky top-0 z-10 ${className}`} {...props}>
      {children}
    </div>
  );
};

export const Sticky: React.FC<StickyProps> = ({
  children,
  onViewportChange,
  className,
  ...props
}) => {
  const stickyContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (onViewportChange) {
      onViewportChange(false);
    }
  }, [onViewportChange]);

  useEffect(() => {
    const scrollTopButton = document.getElementById('scrollup-wrapper');

    if (scrollTopButton) {
      if (stickyContainerRef && stickyContainerRef.current) {
        scrollTopButton.style.transform = `translateY(-${stickyContainerRef.current.clientHeight}px)`;
      }
    }

    return () => {
      if (scrollTopButton) {
        scrollTopButton.style.transform = 'translateY(0)';
      }
    };
    // our Buybox height is dynamic when inside/outside the viewport
  }, [stickyContainerRef?.current?.clientHeight]);

  return (
    <div
      ref={stickyContainerRef}
      className={`fixed bottom-0 left-0 z-10 block w-full ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

export const StickyOutsideViewport: React.FC<StickyProps> = ({
  children,
  className,
  onViewportChange,
  ...props
}) => {
  const onSiteElementRef = useRef<HTMLDivElement>(null);
  const { isIntersecting } = useIntersectionObserver(onSiteElementRef, {
    threshold: 0.1,
    intersectOnce: false,
  });

  useEffect(() => {
    if (onViewportChange) {
      onViewportChange(isIntersecting);
    }
  }, [isIntersecting, onViewportChange]);

  return (
    <>
      <div ref={onSiteElementRef} className={className}>
        {children}
      </div>
      {!isIntersecting && <Sticky className={className}>{children}</Sticky>}
    </>
  );
};
